import classNames from 'classnames';
import { useRouter } from 'next/router';
import React, { FC, useContext, useEffect, useTransition } from 'react';
import { GetNavListResponse } from '@/request/interface/resources';
import useIsMobile from '@/hooks/useIsMobile';
import { traceEvent } from '@wk/wk-gatherer';
import PC from '@/components/Device/PC';
import Search from '@/components/Icon/Search';
import Mobile from '@/components/Device/Mobile';
import { useTranslation } from 'react-i18next';
import { generateNavList } from '../../utils';
import ResourceContext from '../../context';
import ResourceSearch from '../../Common/ResourceSearch';
import useTabSwitch from '../../hooks/useTabSwitch';

const Nav: FC<{ data: GetNavListResponse['content'] }> = ({ data }) => {
  const isMobile = useIsMobile();
  const router = useRouter();
  const { subject } = router.query;
  const navList = generateNavList(data, isMobile);
  const { setQuery } = useContext(ResourceContext);
  const { tabContainerRef, setCurrentIndex, currentTabStyle } = useTabSwitch('category-tab-item');
  const [, startTransition] = useTransition();
  const { i18n } = useTranslation('resources');

  const hasTab = navList.find((nav) => nav.href?.includes(subject as string));
  if (!hasTab && navList?.length) {
    router.push(navList?.[0]?.href || '', undefined, { scroll: false });
  }

  const handleGoPage = (href: string, index: number) => {
    traceEvent({
      data: {
        _event: 'O_LearningWorldList_SubjectTabClick',
        subject,
      },
    });
    startTransition(() => {
      setCurrentIndex(index);
      setQuery({ page: '1' });
      const match = href?.match(/\/resources\/([^/]+)\//);
      const currentSubject = match && match[1];
      router.push(`/resources/${currentSubject}/`, undefined, { scroll: false });
    });
  };

  useEffect(() => {
    if (!navList?.length) {
      return;
    }
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < navList.length; i++) {
      const { href } = navList[i];
      if (href?.includes(subject as string)) {
        setCurrentIndex(i);
      }
    }
  }, []);

  return (
    <nav
      className="box-border flex justify-center mobile:py-[12px] pc:py-[16px]"
      style={{
        background:
          'linear-gradient(90deg, rgba(255, 83, 83, 0.08) 8.55%, rgba(109, 56, 251, 0.08) 61.14%, rgba(0, 157, 255, 0.08) 100%), #FFF',
      }}
    >
      <div ref={tabContainerRef} className="relative flex items-center lg:container mobile:w-[100%] mobile:px-[20px]">
        {navList?.map?.(({ label, href }, index) => {
          return (
            <div
              onClick={() => handleGoPage(href as string, index)}
              key={label}
              className={classNames(
                'category-tab-item group relative z-10 flex cursor-pointer items-center justify-center text-[16px] transition-opacity',
              )}
            >
              <div
                className={classNames(
                  'flex h-[36px] translate-y-0 select-none items-center justify-center border-[2px] border-solid border-transparent px-[12px] text-[14px] lg:px-[16px] xl:h-[43px] xl:px-[24px] xl:text-[16px]',
                )}
              >
                <span
                  className={classNames(
                    href?.includes(subject as string) && 'linearGradient1 font-bold',
                    href?.includes(subject as string) && i18n.language === 'zh' ? 'font-sourceHanSans' : '',
                  )}
                >
                  {label}
                </span>
              </div>
            </div>
          );
        })}
        <div
          style={{ ...currentTabStyle }}
          className={classNames(
            'group absolute top-0 flex cursor-pointer items-center justify-center text-[16px] transition-opacity',
          )}
        >
          <div
            className={classNames(
              'h-full	w-full translate-y-0 select-none items-center justify-center rounded-[12px] border-[2px] border-solid border-[#fff] bg-[linear-gradient(90deg,rgba(255,83,83,0.08)_8.55%,rgba(109,56,251,0.08)_61.14%,rgba(0,157,255,0.08)_100%),linear-gradient(180deg,rgba(255,255,255,0.70)_0%,rgba(255,255,255,0.95)_105.36%)] px-[12px] py-[8px] text-[14px]  font-bold lg:px-[16px] xl:rounded-[16px] xl:px-[24px] xl:py-[12px] xl:text-[16px]',
            )}
          />
        </div>
        <PC>
          <div className="absolute right-0 lg:right-[1.25rem]">
            <ResourceSearch />
          </div>
        </PC>
        <Mobile>
          <button
            onClick={() => router.push('/resources/search/')}
            className="absolute right-[20px] rounded-[99px] border-[1px] border-solid border-[#F0F1FC] bg-white px-[12px] py-[8px]"
          >
            <Search stroke="#8D8FA6" />
          </button>
        </Mobile>
      </div>
    </nav>
  );
};

export default Nav;
